// EAA-1667: The ScreenRouteRule shape and the path matcher moved to
// @ai-content-hub/hooks (packages/hooks/src/permission). Re-export the type here so
// existing @/config/screenPermissions importers (permissions slice/service) keep one
// canonical type instead of a local duplicate that could drift. Only the app-specific
// route table stays. The local findScreenRuleForPath/findScreenRule matchers were
// removed — usePermissions resolves via the core matcher and nothing else used them.
import { PERMISSION_CODES } from '@/constants/permissionCatalog';
import type { ScreenRouteRule } from '@ai-content-hub/hooks';

export type { ScreenRouteRule };

/**
 * Map URL path prefixes to catalog permissions (any match grants access).
 * Fallback when GET /api/permissions/screen-rules/ is unavailable; merged when API returns rules.
 */
export const SCREEN_ROUTE_RULES: readonly ScreenRouteRule[] = [
    // EAA-1842: keyed on the PLATFORM code, not dashboard.read. The dashboard reports
    // cross-tenant metrics and its endpoints admit only platform staff or an API token,
    // never an SSO user — while dashboard.read is held by sys-viewer, so every tenant
    // user was shown a menu item that could only answer 403. No tenant group may hold a
    // platform-only code, so this hides it for tenant callers and keeps it for staff.
    { pathPrefix: '/dashboard', anyOf: [PERMISSION_CODES.PLATFORM_DASHBOARD_READ] },
    { pathPrefix: '/question-bank', anyOf: [PERMISSION_CODES.CONTENT_READ] },
    { pathPrefix: '/content-types', anyOf: [PERMISSION_CODES.CONTENT_TYPE_READ] },
    { pathPrefix: '/documents', anyOf: [PERMISSION_CODES.CONTENT_READ] },
    { pathPrefix: '/tag-types', anyOf: [PERMISSION_CODES.TAG_READ] },
    { pathPrefix: '/tags', anyOf: [PERMISSION_CODES.TAG_READ] },
    { pathPrefix: '/embed/tags', anyOf: [PERMISSION_CODES.TAG_READ] },
    { pathPrefix: '/embed/tag-types', anyOf: [PERMISSION_CODES.TAG_READ] },
    { pathPrefix: '/programs', anyOf: [PERMISSION_CODES.PROGRAM_READ] },
    // EAA-1842: `tenant.tokens.manage` ONLY, matching core/screen_route_defaults.py.
    // This used to also accept `tenant.self.read`, which every sys-* group holds —
    // so the fallback table opened the tenant admin area to viewers and editors,
    // while the served rules (which REPLACE this table once loaded, see
    // createPermissionState) did not. Two answers for one screen, and the one a
    // reader is most likely to find here was the wrong one.
    {
        pathPrefix: '/tenants',
        anyOf: [PERMISSION_CODES.TENANT_TOKENS_MANAGE],
    },
    // EAA-1842: the theme screen is an EDIT surface, so it is gated on the write code
    // (sys-tenant-admin / sys-admin only). It used to accept tenant.settings.read, which
    // sys-viewer holds — viewers were shown the editor and a Save button that 403'd —
    // and tenant.self.write, which sys-editor holds, so editors really could restyle the
    // whole tenant.
    {
        pathPrefix: '/settings/theme',
        anyOf: [PERMISSION_CODES.TENANT_THEME_WRITE],
    },
    // EAA-1212: platform system settings. The backend ships the same rule; the
    // page and the sidebar additionally require platform staff (see
    // `canOpenSystemSettings`), because a path with no server rule is open.
    {
        pathPrefix: '/settings/system',
        anyOf: [PERMISSION_CODES.CONFIG_MANAGE],
    },
] as const;
